import React from "react";
import DOMPurify from "dompurify";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Youtube from "@tiptap/extension-youtube";
import { Bold, Italic, Underline as UIcon, List, ListOrdered, Heading2, Heading3, Link2, Undo2, Redo2, Quote, Code, Eraser, Image as ImgIcon, Youtube as YTIcon } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * Rich text editor for all editable content on the site.
 * Outputs HTML. Use <SafeHTML html={...} /> to render.
 * Supports: bold, italic, underline, strike, H2/H3, lists, quote, code, links, **images** (upload), **YouTube embeds**.
 * Long content scrolls within the editor body (maxHeight default 480px).
 */
export default function RichTextEditor({
  value,
  onChange,
  placeholder = "Écris ton contenu…",
  minHeight = 220,
  maxHeight = 480,
  testId = "richtext-editor",
}) {
  const fileRef = React.useRef(null);
  const [uploading, setUploading] = React.useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] } }),
      Underline,
      Link.configure({ openOnClick: false, HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" } }),
      Image.configure({ HTMLAttributes: { class: "rounded-md border-2 border-black my-3" } }),
      Youtube.configure({ controls: true, nocookie: true, HTMLAttributes: { class: "w-full aspect-video rounded-md border-2 border-black my-3" } }),
    ],
    content: value || "",
    onUpdate: ({ editor: ed }) => onChange?.(ed.getHTML()),
    editorProps: {
      attributes: {
        class: "prose prose-sm sm:prose-base max-w-none focus:outline-none p-3",
        "data-testid": `${testId}-content`,
      },
    },
  });

  // Sync external value changes (e.g. on modal open)
  React.useEffect(() => {
    if (editor && value !== undefined && value !== editor.getHTML()) {
      editor.commands.setContent(value || "", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  if (!editor) return null;

  const Btn = ({ on, active, title, children, testid, disabled }) => (
    <button
      type="button"
      onClick={on}
      title={title}
      disabled={disabled}
      className={`w-8 h-8 inline-flex items-center justify-center border-2 border-black rounded-md text-sm transition disabled:opacity-50 ${active ? "bg-black text-white" : "bg-white hover:bg-cef-yellow"}`}
      data-testid={`rt-${testid}`}
    >
      {children}
    </button>
  );

  const addLink = () => {
    const prev = editor.getAttributes("link").href;
    const url = window.prompt("URL du lien (laisse vide pour retirer)", prev || "https://");
    if (url === null) return;
    if (url === "") return editor.chain().focus().extendMarkRange("link").unsetLink().run();
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const addYoutube = () => {
    const url = window.prompt("Lien YouTube (ex: https://www.youtube.com/watch?v=…)");
    if (!url) return;
    editor.chain().focus().setYoutubeVideo({ src: url, width: 640, height: 360 }).run();
  };

  const onPickImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/storage/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      editor.chain().focus().setImage({ src: data.url, alt: file.name }).run();
      toast.success("Image insérée");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload échoué");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="border-2 border-black rounded-md bg-white" data-testid={testId}>
      <div className="flex flex-wrap gap-1 p-2 border-b-2 border-black bg-cef-paper sticky top-0 z-10">
        <Btn on={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} title="Gras" testid="bold"><Bold className="w-4 h-4" /></Btn>
        <Btn on={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} title="Italique" testid="italic"><Italic className="w-4 h-4" /></Btn>
        <Btn on={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive("underline")} title="Souligné" testid="underline"><UIcon className="w-4 h-4" /></Btn>
        <Btn on={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive("strike")} title="Barré" testid="strike"><Eraser className="w-4 h-4" /></Btn>
        <span className="w-px bg-black/20 mx-1" />
        <Btn on={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} title="Titre H2" testid="h2"><Heading2 className="w-4 h-4" /></Btn>
        <Btn on={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })} title="Titre H3" testid="h3"><Heading3 className="w-4 h-4" /></Btn>
        <span className="w-px bg-black/20 mx-1" />
        <Btn on={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} title="Liste à puces" testid="ul"><List className="w-4 h-4" /></Btn>
        <Btn on={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} title="Liste numérotée" testid="ol"><ListOrdered className="w-4 h-4" /></Btn>
        <Btn on={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive("blockquote")} title="Citation" testid="quote"><Quote className="w-4 h-4" /></Btn>
        <Btn on={() => editor.chain().focus().toggleCode().run()} active={editor.isActive("code")} title="Code" testid="code"><Code className="w-4 h-4" /></Btn>
        <span className="w-px bg-black/20 mx-1" />
        <Btn on={addLink} active={editor.isActive("link")} title="Lien" testid="link"><Link2 className="w-4 h-4" /></Btn>
        <Btn on={() => fileRef.current?.click()} title="Insérer une image" testid="image" disabled={uploading}><ImgIcon className="w-4 h-4" /></Btn>
        <Btn on={addYoutube} title="Insérer une vidéo YouTube" testid="youtube"><YTIcon className="w-4 h-4" /></Btn>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPickImage} data-testid="rt-image-input" />
        <span className="w-px bg-black/20 mx-1" />
        <Btn on={() => editor.chain().focus().undo().run()} title="Annuler" testid="undo"><Undo2 className="w-4 h-4" /></Btn>
        <Btn on={() => editor.chain().focus().redo().run()} title="Rétablir" testid="redo"><Redo2 className="w-4 h-4" /></Btn>
      </div>
      <div style={{ minHeight, maxHeight }} className="overflow-y-auto">
        <EditorContent editor={editor} placeholder={placeholder} />
      </div>
    </div>
  );
}

/** Safe HTML renderer for content produced by RichTextEditor (sanitized via DOMPurify). */
export function SafeHTML({ html, className = "" }) {
  if (!html) return null;
  const clean = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ["p", "br", "strong", "em", "u", "s", "h2", "h3", "ul", "ol", "li", "blockquote", "code", "pre", "a", "span", "img", "iframe", "div"],
    ALLOWED_ATTR: ["href", "target", "rel", "class", "src", "alt", "width", "height", "title", "frameborder", "allow", "allowfullscreen", "loading"],
    ADD_TAGS: ["iframe"],
    ADD_ATTR: ["allow", "allowfullscreen", "frameborder", "scrolling"],
    ALLOW_DATA_ATTR: false,
  });
  // eslint-disable-next-line react/no-danger -- HTML sanitized above via DOMPurify with strict allowlist (ALLOWED_TAGS/ATTR + ALLOW_DATA_ATTR:false). Required to render TipTap output as inline HTML.
  return <div className={`prose prose-sm sm:prose-base max-w-none break-words [&_iframe]:w-full [&_iframe]:aspect-video [&_iframe]:rounded-md [&_iframe]:border-2 [&_iframe]:border-black [&_img]:rounded-md [&_img]:border-2 [&_img]:border-black ${className}`} dangerouslySetInnerHTML={{ __html: clean }} />;
}
